import { PhoneNumberField, type PhoneNumberFieldProps } from '@side/pantry';
import { isEmpty } from 'lodash';
import { type FieldValues, useController } from 'react-hook-form';
import {
  derivePhoneNumber,
  isValueEqualToCountryPrefix,
} from '@/common/utils/phoneNumber';
import type { RHFBoundProps } from './types';

export type PhoneFieldProps<TFieldValues extends FieldValues = FieldValues> =
  Omit<
    PhoneNumberFieldProps,
    'value' | 'onChange' | 'onBlur' | 'error' | 'name'
  > &
    RHFBoundProps<TFieldValues, string | null | undefined> & {
      /** Fires after the field blurs, with the (sanitized) final value. */
      onBlur?: (value: string | null) => void;
    };

/**
 * Phone-number input with country-prefix handling and display formatting.
 * Stores the raw phone-number string (E.164-ish) or `null`. Clears the
 * stored value on blur if the user only typed the country prefix, so a
 * half-entered number doesn't survive as a non-empty string.
 */
export function PhoneField<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  rules,
  shouldUnregister,
  label,
  required,
  helperText,
  onBlur: consumerOnBlur,
  ...rest
}: PhoneFieldProps<TFieldValues>) {
  const {
    field: { value, onChange, onBlur: rhfOnBlur },
    fieldState: { error },
  } = useController({ control, name, rules, shouldUnregister });

  const stringValue = typeof value === 'string' ? value : '';

  return (
    <PhoneNumberField
      {...rest}
      // Override Pantry's hardcoded id="phone-number" so multiple phones
      // on the same page don't collide on aria-labelledby.
      id={name}
      name={name}
      label={label}
      fixedHeight
      fullWidth
      required={required}
      autoComplete="off"
      value={derivePhoneNumber(stringValue)}
      highlight={required && isEmpty(stringValue) ? 'secondary' : undefined}
      error={!!error}
      helperText={error?.message ?? helperText}
      onChange={(next) => onChange(next || null)}
      onBlur={(nextValue) => {
        rhfOnBlur();
        // A "country-prefix only" value (e.g. "+1") means the user opened
        // and abandoned the field — collapse it to `null` so it doesn't
        // round-trip as a non-empty string.
        const hasOnlyPrefix = isValueEqualToCountryPrefix(nextValue);
        const sanitized = hasOnlyPrefix ? null : nextValue || null;
        if (hasOnlyPrefix) onChange(null);
        consumerOnBlur?.(sanitized);
      }}
    />
  );
}
